import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as appActions from "../../store/actions/app";
import * as formActions from "../../store/actions/forms";
import styles from "./NewClaim.module.css";
import { prompt } from "../../constants/messages";
import AutoForm from "../forms/auto-form/AutoForm";
import AutoGlassForm from "../forms/auto-glass-form/AutoGlassForm";
import PropertyForm from "../forms/property-form/PropertyForm";
import WorkersCompForm from "../forms/workers-comp-form/WorkersCompForm";
import PersonalClaim from "../claim-pages/personal-claim/PersonalClaim";
import CommercialClaim from "../claim-pages/commercial-claim/CommercialClaim";
import Letter from "../letter/Letter";
import Footer from "../layout/footer/Footer";
import { COMMERCIAL, PERSONAL, formTypes } from "../../constants/keys";

function NewClaim() {
  const dispatch = useDispatch();
  const policyType = useSelector((state) => state.app.policyType);
  const activePolicy = useSelector((state) => state.policies.activePolicy);
  const lossCauses = useSelector((state) => state.forms.lossCauses);
  const userInfo = useSelector((state) => state.user.userInfo);
  const agentInfo = useSelector((state) => state.user.agentInfo);
  const isAnon = useSelector((state) => state.app.isAnonymous);
  const selectedType = useSelector((state) => state.forms.formType);
  const [selectedClaimType, setSelectedClaimType] = useState(policyType.active);
  const blankSelectedPersonal = { auto: false, glass: false, home: false };
  const blankSelectedCommercial = {
    wc: false,
    cauto: false,
    property: false,
    gl: false,
  };
  const [selectedPersonal, setSelectedPersonal] = useState(
    blankSelectedPersonal
  );
  const [selectedCommercial, setSelectedCommercial] = useState(
    blankSelectedCommercial
  );
  const [form, setForm] = useState(null);

  const [claimDisplay, setClaimDisplay] = useState();
  const autoPolicies = useSelector((state) => state.forms.autoPolicies);
  const homePolicies = useSelector((state) => state.forms.homePolicies);
  const vehiclePolicies = useSelector((state) => state.forms.vehiclePolicies);
  const propertyPolicies = useSelector((state) => state.forms.propertyPolicies);
  const liabilityPolicies = useSelector((state) => state.forms.liabilityPolicies);
  const wcPolicies = useSelector((state) => state.forms.wcPolicies);
  const personalPolicies = useSelector(state => [].concat(state.forms.autoPolicies)
    .concat(state.forms.homePolicies)
   
  );
  const commercialPolicies = useSelector(state => [].concat(state.forms.vehiclePolicies)
    .concat(state.forms.propertyPolicies)
    .concat(state.forms.liabilityPolicies)
    .concat(state.forms.wcPolicies)
  );

  const showForm = () => {
    if (selectedType === formTypes.Auto) {
      setSelectedPersonal({ ...blankSelectedPersonal, auto: true });
      setForm(<AutoForm />);
    } else if (selectedType === formTypes.AutoGlass) {
      setSelectedPersonal({ ...blankSelectedPersonal, glass: true });
      setForm(<AutoGlassForm />);
    } else if (selectedType === formTypes.Home) {
      setSelectedPersonal({ ...blankSelectedPersonal, home: true });
      setForm(<PropertyForm />);
    } else if (selectedType === formTypes.WorkersComp) {
      setSelectedCommercial({ ...blankSelectedCommercial, wc: true });
      setForm(<WorkersCompForm />);
    } else if (selectedType === formTypes.Vehicle) {
      setSelectedCommercial({ ...blankSelectedCommercial, cauto: true });
      setForm(<AutoForm />);
    } else if (selectedType === formTypes.Property) {
      setSelectedCommercial({ ...blankSelectedCommercial, property: true });
      setForm(<PropertyForm />);
    } else if (selectedType === formTypes.Liability) {
      setSelectedCommercial({ ...blankSelectedCommercial, gl: true });
      setForm(<PropertyForm />);
    } else {
      setSelectedPersonal({ ...blankSelectedPersonal });
      setSelectedCommercial({ ...blankSelectedCommercial });
      setForm(null);
    }
  };

  const selectType = (name) => {
    dispatch(formActions.setFormType(name));
    dispatch(formActions.clearPolicyAssets());
  };

  useEffect(() => {
    if (lossCauses?.length < 1) {
      dispatch(formActions.getLossCauses());
    }
  }, []);

  useEffect(() => {
    if (policyType.active !== selectedClaimType) {
      dispatch(formActions.setFormType(""));
    }

    if (!userInfo || isAnon) {
      dispatch(appActions.setButtonHeader(false));
    }

    setSelectedClaimType(policyType.active);

    if (!activePolicy && !isAnon) {
        if ((policyType.active === PERSONAL && personalPolicies?.length < 1) ||
            (policyType.active === COMMERCIAL && commercialPolicies?.length < 1)) {
          dispatch(formActions.getAllPolicies());
        }
    }

    return () => {
      dispatch(appActions.setButtonHeader(true));
    };
  }, [policyType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
   if (activePolicy !== null) {
    dispatch(formActions.setFormType(activePolicy.selector));
   }
    if (activePolicy?.selector !== formTypes.PropertyLiability) {
      activePolicy !== null &&
        dispatch(
          appActions.setVisibleSelectors(false, false, false, false, false, false)
        );
    } else if (activePolicy?.selector) {
      dispatch(appActions.setVisibleSelectors(false, true, true, false, false, false));
    }
  }, [activePolicy]);

  useEffect(() => {
    if (selectedClaimType === PERSONAL) {
      setClaimDisplay(
        <PersonalClaim onTypeSelect={selectType} selected={selectedPersonal} />
      );
    } else if (selectedClaimType === COMMERCIAL) {
      setClaimDisplay(
        <CommercialClaim
         onTypeSelect={selectType}
          selected={selectedCommercial}
        />
      );
    }
  }, [selectedClaimType, selectedPersonal, selectedCommercial])



  useEffect(() => {
    showForm();
  }, [selectedType]);

  useEffect(() => {
    if (!activePolicy) {
      dispatch(appActions.setVisibleSelectors(
        autoPolicies?.length > 0,
        propertyPolicies?.length > 0,
        liabilityPolicies?.length > 0,
        homePolicies?.length > 0,
        wcPolicies?.length > 0,
        vehiclePolicies?.length > 0
      ));
    }
  }, [dispatch, activePolicy, autoPolicies, homePolicies, vehiclePolicies, propertyPolicies, liabilityPolicies, wcPolicies])

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>New Claim</h1>
      {(userInfo || isAnon) && <Letter />}

      <div className={styles.promptBox}>{prompt}</div>

      {(userInfo ||
        (agentInfo &&
          activePolicy?.selector === formTypes.PropertyLiability)) && (
        <h2 className={styles.heading}>
          What type of claim are you reporting?
        </h2>
      )}

      {claimDisplay}

      {form}

      <Footer />
    </div>
  );
}

export default NewClaim;
